import React from 'react';
import styled, { css } from 'styled-components';
import { Classable, Shapeable } from '@shapeable/types';
import { breakpoints, theme } from '@shapeable/theme';
import { classNames } from '@shapeable/utils';
import { FilterToggleBar, FilterToggleItem, useView } from '@shapeable/ui';
import { usePublicationFilters } from '../../hooks/use-publication-filters';

const cls = classNames('publication-type-filter-bar');

// PublicationAnimatedGrid fades out rows when types are deselected, then layout-animates
// the rest upward. A second toggle before that exit finishes (framer-motion 6) can leave
// faded rows mounted at opacity 0 but still taking space and clicks — a blank-looking grid.
// Briefly ignore further type-toggle clicks so one filter transition can complete.
const TOGGLE_LOCK_MS = 500;

export type PublicationTypeCounts = Record<string, number> & {
  all: number;
};

export type PublicationTypeFilterBarProps = Classable & {
  view: string;
  counts: PublicationTypeCounts;
  toolbarEnd?: React.ReactNode;
};

// -------- Styles -------->

type FilterBarProps = {
  $locked?: boolean;
};

const RootStyles = breakpoints({
  base: css`
    width: 100%;

    ${({ $locked }: FilterBarProps) => $locked && css`
      .shp--toggle-pill {
        pointer-events: none;
      }
    `}
  `,
});

const FilterToggleBarStyles = breakpoints({
  base: css`
    width: 100%;
  `,
});

const My = {
  Root: styled.div<FilterBarProps>`${RootStyles}`,
  FilterToggleBar: styled(FilterToggleBar)`${FilterToggleBarStyles}`,
};

export const PublicationTypeFilterBar: Shapeable.FC<PublicationTypeFilterBarProps> = (props) => {
  const { className, view, counts, toolbarEnd } = props;

  const { typeToggles } = usePublicationFilters();
  const { selectedTypes = [], types = [], setTypes } = useView(view);
  const [isLocked, setIsLocked] = React.useState(false);
  const lockTimer = React.useRef<number>();

  React.useEffect(() => {
    return () => window.clearTimeout(lockTimer.current);
  }, []);

  const allTypeNames = React.useMemo(
    () => typeToggles.map((toggle) => toggle.name),
    [typeToggles],
  );

  const setPublicationTypes = React.useCallback((names: string[]) => {
    setTypes(names, names.length ? names : allTypeNames);
  }, [allTypeNames, setTypes]);

  const onToggleChange = React.useCallback((names: string[]) => {
    if (isLocked) {
      return;
    }

    setPublicationTypes(names);
    setIsLocked(true);
    window.clearTimeout(lockTimer.current);
    lockTimer.current = window.setTimeout(() => setIsLocked(false), TOGGLE_LOCK_MS);
  }, [isLocked, setPublicationTypes]);

  // Repair partial view state (e.g. after topic filter updates) without resetting user selection.
  React.useEffect(() => {
    if (selectedTypes.length > 0 && types.length === 0) {
      setPublicationTypes(selectedTypes);
    }
  }, [selectedTypes.join(','), setPublicationTypes, types.length]);

  const items: FilterToggleItem[] = React.useMemo(
    () => typeToggles.map((toggle) => ({
      name: toggle.name,
      label: toggle.label,
      count: counts[toggle.name],
    })),
    [counts, typeToggles],
  );

  return (
    <My.Root className={cls.name(className)} $locked={isLocked}>
      <My.FilterToggleBar
        activeNames={selectedTypes}
        allCount={counts.all}
        showAll={false}
        buttonLabel="Publication types"
        items={items}
        onChange={onToggleChange}
        toolbarEnd={toolbarEnd}
      />
    </My.Root>
  );
};

PublicationTypeFilterBar.cls = cls;
